import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getShapeQuestions } from '../utils/shapeQuestions';
import { playCorrectSound, playWrongSound } from '../utils/soundEffects';
import { X, ChevronRight } from 'lucide-react';
import { ShapeSVG } from '../components/game/ShapeSVG';
import confetti from 'canvas-confetti';

const SHAPES_BG = 'https://static.prod-images.emergentagent.com/jobs/0e7ec82b-be21-4167-ae09-299c58207254/images/b2f79a00e3e7026c8cc3c707837c5f6bc69c299688fbfbcfe5b474f82206f985.png';

const OPTION_COLORS = [
  'bg-red-500 border-red-700 hover:bg-red-400',
  'bg-blue-500 border-blue-700 hover:bg-blue-400',
  'bg-amber-500 border-amber-700 hover:bg-amber-400',
  'bg-emerald-500 border-emerald-700 hover:bg-emerald-400',
];

export default function ShapesPlayPage() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const scoreRef = useRef(0);

  useEffect(() => { scoreRef.current = score; }, [score]);

  useEffect(() => {
    setQuestions(getShapeQuestions(5));
  }, []); // mount-only: generate once

  const currentQ = questions[currentIndex];

  const handleOptionClick = (idx) => {
    if (showAnswer) return;
    setSelectedOption(idx);
    setShowAnswer(true);

    if (idx === currentQ.correctIndex) {
      setScore((s) => s + 1);
      playCorrectSound();
      confetti({ particleCount: 60, spread: 70, origin: { y: 0.7 }, colors: ['#ef4444', '#3b82f6', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899'] });
    } else {
      playWrongSound();
    }
  };

  const handleNext = () => {
    if (currentIndex + 1 >= questions.length) {
      navigate(`/shapes/results?score=${scoreRef.current}&total=${questions.length}`);
      return;
    }
    setCurrentIndex((i) => i + 1);
    setSelectedOption(null);
    setShowAnswer(false);
  };

  if (!currentQ) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundImage: `url(${SHAPES_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <p className="font-heading text-2xl text-gray-400">Loading...</p>
      </div>
    );
  }

  const progress = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundImage: `url(${SHAPES_BG})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
      {/* Top bar */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-white px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <span data-testid="shapes-score" className="font-heading text-3xl font-black text-gray-900">
            {score}/{questions.length}
          </span>
          <span data-testid="shapes-counter" className="font-body text-sm font-bold text-gray-500 bg-white px-3 py-1 rounded-full">
            {currentIndex + 1}/{questions.length}
          </span>
          <button data-testid="shapes-quit" onClick={() => navigate('/shapes')} className="p-2 rounded-full hover:bg-white/80 transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Progress bar - rainbow */}
      <div className="h-3 bg-white/50">
        <div
          data-testid="shapes-progress"
          className="h-full rounded-r-full transition-all duration-500 ease-out"
          style={{
            width: `${progress}%`,
            background: 'linear-gradient(90deg, #ef4444, #f59e0b, #10b981, #3b82f6, #8b5cf6)',
          }}
        />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center max-w-xl mx-auto w-full px-4 py-6">
        <p className="text-center font-heading text-2xl sm:text-3xl font-black text-gray-900 mb-6">
          What shape is this?
        </p>

        {/* Shape Display */}
        <div className="bg-white rounded-3xl shadow-lg border-4 border-white p-8 mb-6 flex items-center justify-center w-48 h-48 sm:w-56 sm:h-56 mx-auto">
          <div data-testid="shape-display" className="w-full h-full">
            <ShapeSVG shapeId={currentQ.shape.id} color={currentQ.shape.color} />
          </div>
        </div>

        {/* Options */}
        <div className="grid grid-cols-2 gap-3 w-full mb-4">
          {currentQ.options.map((opt, i) => {
            const optKey = `${currentQ.id}-opt-${opt}`;
            let extraClass = '';
            if (showAnswer) {
              if (i === currentQ.correctIndex) {
                extraClass = 'ring-4 ring-green-400 scale-[1.02]';
              } else if (i === selectedOption && i !== currentQ.correctIndex) {
                extraClass = 'opacity-60 ring-4 ring-red-400';
              } else {
                extraClass = 'opacity-40';
              }
            }

            return (
              <button
                key={optKey}
                data-testid={`shapes-option-${i}`}
                onClick={() => handleOptionClick(i)}
                disabled={showAnswer}
                className={`${OPTION_COLORS[i]} text-white font-black text-base sm:text-lg py-4 px-4 rounded-2xl border-b-[5px] ${!showAnswer ? 'active:border-b-0 active:translate-y-[5px]' : ''} transition-all shadow-md text-center ${extraClass}`}
              >
                {opt}
              </button>
            );
          })}
        </div>

        {/* Feedback + Next */}
        {showAnswer && (
          <div className="text-center animate-pop-in">
            {selectedOption === currentQ.correctIndex ? (
              <p data-testid="shapes-feedback-correct" className="font-heading text-2xl font-black text-green-600 mb-3">
                Correct!
              </p>
            ) : (
              <p data-testid="shapes-feedback-wrong" className="font-heading text-base font-bold text-red-500 mb-3">
                It&apos;s a <span className="text-green-700 font-black">{currentQ.shape.name}</span>!
              </p>
            )}
            <button
              data-testid="shapes-next"
              onClick={handleNext}
              className="bg-gradient-to-r from-pink-500 to-violet-500 text-white font-black text-base py-3 px-8 rounded-full border-b-[4px] border-violet-700 active:border-b-0 active:translate-y-[4px] transition-all hover:from-pink-400 hover:to-violet-400 inline-flex items-center gap-2"
            >
              {currentIndex + 1 >= questions.length ? 'See Results' : 'Next Shape'}
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
